import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Slider } from "antd";

/**
 * Recorte de imagem para o formulário de questão.
 *
 * Sem dependência de cropper: um retângulo em % da imagem exibida, arrastável
 * e redimensionável pelos cantos — o mesmo desenho do OcclusionEditor e do
 * PdfAnnotator, que já usam essa convenção de coordenadas percentuais.
 *
 * `src` DEVE ser um object URL (blob local). Imagem remota direto no canvas
 * seria cross-origin (a API vive em outro host) e o canvas ficaria tainted —
 * quem chama busca o blob primeiro.
 *
 * `onDone(blob)` sempre entrega PNG re-encodado pelo canvas; "Usar inteira"
 * entrega `null` e o chamador decide manter o arquivo original.
 */
export interface ImageCropModalProps {
  open: boolean;
  /** Object URL da imagem (blob local — nunca URL remota). */
  src: string | null;
  title?: string;
  onCancel: () => void;
  /** Blob PNG do recorte, ou null para "usar a imagem inteira". */
  onDone: (blob: Blob | null) => void;
}

type Rect = { x: number; y: number; w: number; h: number }; // % da imagem exibida
type Mode =
  | { kind: "idle" }
  | { kind: "move"; startX: number; startY: number; origin: Rect }
  // resize e draw compartilham a matemática: um ponto fixo, o ponteiro no
  // canto oposto. No draw o ponto fixo é onde o arrasto começou.
  | { kind: "stretch"; fixedX: number; fixedY: number };

const FULL: Rect = { x: 0, y: 0, w: 100, h: 100 };
const MIN = 4; // % mínimo de lado — evita recorte invisível

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export default function ImageCropModal({
  open,
  src,
  title = "Ajustar imagem",
  onCancel,
  onDone,
}: ImageCropModalProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [rect, setRect] = useState<Rect>(FULL);
  const modeRef = useRef<Mode>({ kind: "idle" });
  const [applying, setApplying] = useState(false);
  // Escala de saída (0.1–1): reduz a imagem exportada além do recorte.
  const [scale, setScale] = useState(1);
  // Dimensões naturais, para o leitor de tamanho ao vivo.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  // Largura exibida a 100%, medida no load — o slider redimensiona o preview
  // a partir dela, para a escala ser vista e não só lida em números.
  const [baseW, setBaseW] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setRect(FULL);
      setScale(1);
      setNatural(null);
      setBaseW(null);
    }
  }, [open, src]);

  const toPercent = useCallback((e: { clientX: number; clientY: number }) => {
    const box = imgRef.current?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) return { x: 0, y: 0 };
    return {
      x: clamp(((e.clientX - box.left) / box.width) * 100, 0, 100),
      y: clamp(((e.clientY - box.top) / box.height) * 100, 0, 100),
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent, hit: "area" | "nw" | "ne" | "sw" | "se") => {
    e.preventDefault();
    // Sem isto o pointerdown do canto BORBULHA até a área, que reavalia o
    // ponto (dentro do retângulo) e sobrescreve o modo com "move" — o resize
    // nunca chegava a acontecer.
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = toPercent(e);
    if (hit === "area") {
      const inside =
        p.x >= rect.x && p.x <= rect.x + rect.w && p.y >= rect.y && p.y <= rect.y + rect.h;
      // Dentro: arrasta o recorte. Fora: começa um retângulo novo ali.
      modeRef.current = inside
        ? { kind: "move", startX: p.x, startY: p.y, origin: rect }
        : { kind: "stretch", fixedX: p.x, fixedY: p.y };
      return;
    }
    // Canto: o oposto fica fixo, o arrastado segue o ponteiro.
    modeRef.current = {
      kind: "stretch",
      fixedX: hit === "nw" || hit === "sw" ? rect.x + rect.w : rect.x,
      fixedY: hit === "nw" || hit === "ne" ? rect.y + rect.h : rect.y,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const mode = modeRef.current;
    if (mode.kind === "idle") return;
    e.preventDefault();
    const p = toPercent(e);

    if (mode.kind === "move") {
      const { origin } = mode;
      setRect({
        ...origin,
        x: clamp(origin.x + (p.x - mode.startX), 0, 100 - origin.w),
        y: clamp(origin.y + (p.y - mode.startY), 0, 100 - origin.h),
      });
      return;
    }

    const x1 = Math.min(mode.fixedX, p.x);
    const x2 = Math.max(mode.fixedX, p.x);
    const y1 = Math.min(mode.fixedY, p.y);
    const y2 = Math.max(mode.fixedY, p.y);
    setRect({
      x: x1,
      y: y1,
      w: Math.max(MIN, x2 - x1),
      h: Math.max(MIN, y2 - y1),
    });
  };

  const onPointerUp = () => {
    modeRef.current = { kind: "idle" };
  };

  // Tamanho de saída ao vivo — recorte × escala, em pixels reais.
  const outW = natural ? Math.max(1, Math.round((rect.w / 100) * natural.w * scale)) : null;
  const outH = natural ? Math.max(1, Math.round((rect.h / 100) * natural.h * scale)) : null;

  const exportRect = async (r: Rect) => {
    const img = imgRef.current;
    if (!img) return;
    // Nada a fazer: sem recorte e sem redução — o original segue como está.
    if (r.w > 99 && r.h > 99 && r.x < 0.5 && r.y < 0.5 && scale === 1) {
      onDone(null);
      return;
    }
    setApplying(true);
    try {
      const sx = (r.x / 100) * img.naturalWidth;
      const sy = (r.y / 100) * img.naturalHeight;
      const sw = Math.max(1, (r.w / 100) * img.naturalWidth);
      const sh = Math.max(1, (r.h / 100) * img.naturalHeight);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(sw * scale));
      canvas.height = Math.max(1, Math.round(sh * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas 2d indisponível");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      if (!blob) throw new Error("toBlob falhou");
      onDone(blob);
    } finally {
      setApplying(false);
    }
  };

  const apply = () => exportRect(rect);

  const handle = (corner: "nw" | "ne" | "sw" | "se") => {
    const pos: Record<string, string> = {
      nw: "left-0 top-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize",
      ne: "right-0 top-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize",
      sw: "left-0 bottom-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize",
      se: "right-0 bottom-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize",
    };
    return (
      <span
        key={corner}
        onPointerDown={(e) => onPointerDown(e, corner)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`absolute z-10 h-3 w-3 rounded-full border border-brand bg-surface shadow ${pos[corner]}`}
      />
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={title}
      width={720}
      destroyOnClose
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 rounded-input border border-edge bg-surface px-4 text-sm font-medium text-ink-secondary hover:bg-surface-hover"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={applying}
            className="h-9 rounded-input bg-brand px-4 text-sm font-semibold text-brand-contrast hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {applying ? "Aplicando…" : "Aplicar"}
          </button>
        </div>
      }
    >
      <p className="mb-2 mt-0 text-[13px] text-ink-muted">
        Arraste os cantos para ajustar, o miolo para mover — ou desenhe um
        retângulo novo arrastando fora da seleção.
      </p>
      <div className="mb-3 flex items-center gap-3">
        <span className="shrink-0 text-[13px] text-ink-muted">Tamanho</span>
        <Slider
          min={10}
          max={100}
          step={5}
          value={Math.round(scale * 100)}
          onChange={(v: number) => setScale(v / 100)}
          tooltip={{ formatter: (v) => `${v}%` }}
          className="min-w-0 flex-1"
        />
        <span className="w-28 shrink-0 text-right text-[13px] tabular-nums text-ink-muted">
          {outW && outH ? `${outW} × ${outH}px` : "—"}
        </span>
      </div>
      {src ? (
        <div className="flex justify-center overflow-auto rounded-input bg-surface-muted p-1.5">
          <div
            className="relative inline-block touch-none select-none"
            onPointerDown={(e) => onPointerDown(e, "area")}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            <img
              ref={imgRef}
              src={src}
              alt="Imagem para ajuste"
              draggable={false}
              onLoad={(e) => {
                setNatural({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                });
                setBaseW(e.currentTarget.offsetWidth);
              }}
              // Depois de medida a largura de 100%, o slider encolhe o
              // preview de verdade; as overlays em % acompanham sozinhas.
              className={baseW ? "block" : "block max-h-[60vh] max-w-full"}
              style={baseW ? { width: Math.max(1, Math.round(baseW * scale)) } : undefined}
            />
            {/* Véu nas quatro faixas fora do recorte. */}
            {[
              { left: 0, top: 0, width: "100%", height: `${rect.y}%` },
              { left: 0, top: `${rect.y + rect.h}%`, width: "100%", bottom: 0 },
              { left: 0, top: `${rect.y}%`, width: `${rect.x}%`, height: `${rect.h}%` },
              {
                left: `${rect.x + rect.w}%`,
                top: `${rect.y}%`,
                right: 0,
                height: `${rect.h}%`,
              },
            ].map((style, i) => (
              <span
                key={i}
                aria-hidden
                className="pointer-events-none absolute bg-black/50"
                style={style as React.CSSProperties}
              />
            ))}
            <div
              className="absolute cursor-move border border-brand"
              style={{
                left: `${rect.x}%`,
                top: `${rect.y}%`,
                width: `${rect.w}%`,
                height: `${rect.h}%`,
              }}
            >
              {(["nw", "ne", "sw", "se"] as const).map(handle)}
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
